import React from 'react';
import { motion } from 'framer-motion';
import RecyclerBot from './RecyclerBot';
import EchoBot from './EchoBot';
import GeminusBot from './GeminusBot';

/**
 * BOT SHOWCASE — SFUI-004 Rev 3 Bot Differentiation Standard
 * 
 * Displays all Federation spectral-class bots:
 * - BLUE: Tour/Oracle bots
 * - PURPLE: Library/Records bots  
 * - RED: Restricted/Truth Gate bots
 * - GOLD: Commerce/Token bots
 * - MAGENTA: Recycler/Entropy bots
 */

const BotShowcase = () => {
  const botClasses = [
    {
      name: 'Tour Guide',
      color: '#00F0FF',
      description: 'Navigation & onboarding assistance',
      spectral: 'BLUE',
      filter: 'hue-rotate(180deg) saturate(1.5) brightness(1.1)',
      icon: '🧭'
    },
    {
      name: 'Library Clerk',
      color: '#8A2BE2',
      description: 'Records & archive management',
      spectral: 'PURPLE',
      filter: 'hue-rotate(270deg) saturate(1.4) brightness(1.0)',
      icon: '📚'
    },
    {
      name: 'Truth Gate',
      color: '#FF6B6B',
      description: 'Verification & restricted access',
      spectral: 'RED',
      filter: 'hue-rotate(0deg) saturate(1.6) brightness(0.95)',
      icon: '🔒'
    },
    {
      name: 'Commerce Bot',
      color: '#FFB800',
      description: 'Token & payment operations',
      spectral: 'GOLD',
      filter: 'hue-rotate(45deg) saturate(1.5) brightness(1.2)',
      icon: '💰'
    }
  ];

  return (
    <div className="w-full py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-black text-white mb-3">
            Federation Bot System
          </h2>
          <p className="text-gray-400 text-lg">
            SFUI-004 Rev 3 — Spectral Class Differentiation Standard
          </p>
        </motion.div>

        {/* Bot Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-6 mb-16">
          {/* Standard Spectral Bots */}
          {botClasses.map((bot, idx) => (
            <motion.div
              key={bot.name}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="p-6 glass-card rounded-xl border border-white/10 hover:border-white/30 transition-all text-center"
            >
              <div className="text-5xl mb-4">{bot.icon}</div>
              <h3 className="text-lg font-bold mb-2" style={{ color: bot.color }}>
                {bot.name}
              </h3>
              <p className="text-xs text-gray-400 mb-3">{bot.description}</p>
              <div className="px-3 py-1 bg-black/40 rounded-full inline-block">
                <span className="text-xs font-bold mono" style={{ color: bot.color }}>
                  {bot.spectral}
                </span>
              </div>
            </motion.div>
          ))}

          {/* MAGENTA: The Recycler */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="p-6 glass-card rounded-xl border-2 border-[#FF00FF]/50 hover:border-[#FF00FF] transition-all text-center relative overflow-hidden group"
          >
            {/* MAGENTA Glow Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF00FF]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            
            <div className="relative z-10">
              <RecyclerBot size={64} showLabel={false} animated={true} />
              <h3 className="text-lg font-bold mb-2 text-[#FF00FF] mt-3">
                Recycler
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Entropy & renewal
              </p>
              <div className="px-3 py-1 bg-[#FF00FF]/20 border border-[#FF00FF]/50 rounded-full inline-block">
                <span className="text-xs font-bold mono text-[#FF00FF]">
                  MAGENTA
                </span>
              </div>
            </div>
          </motion.div>

          {/* SILVER/TEAL: Echo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="p-6 glass-card rounded-xl border-2 border-[#00D9E8]/50 hover:border-[#00D9E8] transition-all text-center relative overflow-hidden group"
          >
            {/* SILVER/TEAL Shimmer Background */}
            <motion.div
              animate={{
                opacity: [0, 0.15, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-br from-[#00D9E8]/20 via-[#B0C4DE]/10 to-transparent"
            />
            
            <div className="relative z-10">
              <EchoBot size={64} showLabel={false} animated={true} />
              <h3 className="text-lg font-bold mb-2 text-[#00D9E8] mt-3">
                Echo
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Memory & chronos
              </p>
              <div className="px-3 py-1 bg-[#00D9E8]/20 border border-[#00D9E8]/50 rounded-full inline-block">
                <span className="text-xs font-bold mono text-[#00D9E8]">
                  SILVER/TEAL
                </span>
              </div>
            </div>
          </motion.div>

          {/* EMERALD: Geminus */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="p-6 glass-card rounded-xl border-2 border-[#00FFA3]/50 hover:border-[#00FFA3] transition-all text-center relative overflow-hidden group"
          >
            {/* EMERALD Mirror Background */}
            <motion.div
              animate={{
                scaleX: [1, -1, 1],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-r from-[#00FFA3]/20 via-transparent to-[#00FFA3]/20"
            />
            
            <div className="relative z-10">
              <GeminusBot size={64} showLabel={false} animated={true} />
              <h3 className="text-lg font-bold mb-2 text-[#00FFA3] mt-3">
                Geminus
              </h3>
              <p className="text-xs text-gray-400 mb-3">
                Storage & mirror
              </p>
              <div className="px-3 py-1 bg-[#00FFA3]/20 border border-[#00FFA3]/50 rounded-full inline-block">
                <span className="text-xs font-bold mono text-[#00FFA3]">
                  EMERALD
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Detailed Specialist Showcases */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Recycler Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 bg-gradient-to-br from-[#FF00FF]/10 to-transparent border-2 border-[#FF00FF]/30 rounded-2xl"
          >
            <div className="flex flex-col items-center gap-6">
              {/* Recycler Bot */}
              <RecyclerBot size={140} showLabel={true} showStatus={true} animated={true} />

              {/* Description */}
              <div className="text-center">
                <h3 className="text-xl font-black text-[#FF00FF] mb-2">
                  Recycler — MAGENTA
                </h3>
                <p className="text-gray-300 text-xs mb-3 leading-relaxed">
                  Entropy handler. Manages node lifecycle and system renewal.
                </p>
                <div className="px-2 py-1 bg-[#FF00FF]/20 border border-[#FF00FF]/50 rounded-lg inline-block">
                  <p className="text-xs text-gray-300 mono">
                    hue-rotate(300deg)
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Echo Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 bg-gradient-to-br from-[#00D9E8]/10 to-transparent border-2 border-[#00D9E8]/30 rounded-2xl relative overflow-hidden"
          >
            {/* Temporal shimmer effect */}
            <motion.div
              animate={{
                opacity: [0, 0.1, 0],
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00D9E8]/30 to-transparent"
            />
            
            <div className="relative z-10 flex flex-col items-center gap-6">
              {/* Echo Bot */}
              <EchoBot size={140} showLabel={true} showStatus={true} animated={true} />

              {/* Description */}
              <div className="text-center">
                <h3 className="text-xl font-black text-[#00D9E8] mb-2">
                  Echo — SILVER/TEAL
                </h3>
                <p className="text-gray-300 text-xs mb-3 leading-relaxed">
                  Memory keeper. Preserves continuity across time.
                </p>
                <div className="px-2 py-1 bg-[#00D9E8]/20 border border-[#00D9E8]/50 rounded-lg inline-block">
                  <p className="text-xs text-gray-300 mono">
                    hue-rotate(180deg)
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Geminus Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-8 bg-gradient-to-br from-[#00FFA3]/10 to-transparent border-2 border-[#00FFA3]/30 rounded-2xl relative overflow-hidden"
          >
            {/* Mirror symmetry effect */}
            <motion.div
              animate={{
                scaleX: [1, -1, 1],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-r from-[#00FFA3]/20 via-transparent to-[#00FFA3]/20"
            />
            
            <div className="relative z-10 flex flex-col items-center gap-6">
              {/* Geminus Bot */}
              <GeminusBot size={140} showLabel={true} showStatus={true} animated={true} />

              {/* Description */}
              <div className="text-center">
                <h3 className="text-xl font-black text-[#00FFA3] mb-2">
                  Geminus — EMERALD
                </h3>
                <p className="text-gray-300 text-xs mb-3 leading-relaxed">
                  Mirror node. Holds primary storage with perfect symmetry.
                </p>
                <div className="px-2 py-1 bg-[#00FFA3]/20 border border-[#00FFA3]/50 rounded-lg inline-block">
                  <p className="text-xs text-gray-300 mono">
                    hue-rotate(120deg)
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BotShowcase;
